import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getSymptomsAction,
  deleteSymptomAction,
} from "../../../store/actions/SymptomAction";
import swal from "sweetalert";
import Loader from "../Common/Loader";

const Symptoms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const symptoms = useSelector((state) => state.symptoms.symptoms || []);
  const num_rows = useSelector((state) => state.symptoms.num_rows || 0);

  const [searchQueries, setSearchQueries] = useState({
    symptom_name: "",
    symptom_status: "",
  });

  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");
  const [sortField, setSortField] = useState("symptom_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSymptoms = async () => {
      setLoading(true);
      setError("");
      try {
        await fetchSymptomsFromServer();
      } catch (err) {
        console.error("Failed to fetch symptoms:", err);
        setError("Failed to load symptoms.");
      } finally {
        setLoading(false);
      }
    };

    fetchSymptoms();
  }, [dispatch]);

  const fetchSymptomsFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = "symptom_name",
    sort_order = "asc"
  ) => {
    try {
      await dispatch(
        getSymptomsAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (err) {
      console.error("Failed to fetch symptoms:", err);
      setError("Failed to load symptoms.");
    }
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set the current field
    const clearedQueries = {
      symptom_name: "",
      symptom_status: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    if (keyword.trim()) {
      setActiveSearchField(field);
      setActiveSearchKeyword(keyword);
      fetchSymptomsFromServer(field, keyword, true, 0, sortField, sortOrder);
    } else {
      setActiveSearchField("");
      setActiveSearchKeyword("");
      fetchSymptomsFromServer("", "", true, 0, sortField, sortOrder);
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    fetchSymptomsFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      0,
      field,
      order
    );
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchSymptomsFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      newPage,
      sortField,
      sortOrder
    );
  };

  const handleEdit = (symptom) => {
    navigate(`/symptoms/update-symptom/${symptom.symptom_id}`, {
      state: { symptom },
    });
  };

  const handleDelete = (symptom) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this symptom!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteSymptomAction({ symptom_id: symptom.symptom_id }));
          // Refresh the current page after deletion
          await fetchSymptomsFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete symptom");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const totalPages = Math.ceil(num_rows / rowsPerPage);
  const startEntry = num_rows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, num_rows);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/symptoms/add-symptom"
            className="btn btn-primary btn-rounded"
          >
            + Add Symptom
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleSort("symptom_name")}
                            style={{ cursor: "pointer" }}
                          >
                            Symptom Name{" "}
                            {sortField === "symptom_name" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.symptom_name}
                              onChange={(e) => handleSearch(e, "symptom_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Symptom Name"
                            />
                          </th>
                          <th
                            onClick={() => handleSort("symptom_status")}
                            style={{ cursor: "pointer" }}
                          >
                            Status{" "}
                            {sortField === "symptom_status" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.symptom_status}
                              onChange={(e) => handleSearch(e, "symptom_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {symptoms.map((symptom) => (
                          <tr key={symptom.symptom_id}>
                            <td>{symptom.symptom_name}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${
                                  symptom.symptom_status.toLowerCase() === "active"
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}
                              >
                                {symptom.symptom_status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex">
                                <span
                                  className="edit me-4"
                                  onClick={() => handleEdit(symptom)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Symptom"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(symptom)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Symptom"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {symptoms.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No symptoms found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {startEntry} to {endEntry} of {num_rows} entries
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        onClick={() =>
                          handlePageChange(Math.min(currentPage + 1, totalPages - 1))
                        }
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor:
                            currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Symptoms;
